import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export type NativeInstallOutcome = 'accepted' | 'dismissed' | 'unavailable';

export interface InstallAppDialogData {
  appName: string;
  logo: string;
  themeColor: string;
  isIos: boolean;
  tryNativeInstall?: () => Promise<NativeInstallOutcome>;
  /** Leaves Android in-app browsers without forcing Chrome (so an installed PWA can open). */
  openOutsideInAppBrowser?: () => void;
}

@Component({
  selector: 'app-install-app-dialog',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './install-app-dialog.component.html',
  styleUrl: './install-app-dialog.component.scss',
})
export class InstallAppDialogComponent {
  logoFailed = false;
  showSteps = false;
  private readonly userAgent = window.navigator.userAgent || '';

  constructor(
    public dialogRef: MatDialogRef<InstallAppDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: InstallAppDialogData,
  ) {}

  get appName(): string {
    return (this.data?.appName || 'Passbook').trim();
  }

  get themeColor(): string {
    return this.data?.themeColor || '#EB1F27';
  }

  get logo(): string {
    return this.data?.logo || '';
  }

  get initial(): string {
    return this.appName.charAt(0).toUpperCase() || 'P';
  }

  get shortName(): string {
    return this.appName.length > 12 ? `${this.appName.slice(0, 11)}…` : this.appName;
  }

  /** iPhone, iPad, and iPadOS. Every iOS browser uses the Share sheet to install. */
  get isIos(): boolean {
    return this.data?.isIos === true;
  }

  get isAndroid(): boolean {
    return /Android/i.test(this.userAgent);
  }

  /** Facebook, Instagram, WhatsApp and other in-app browsers cannot install a home-screen app. */
  get isInAppBrowser(): boolean {
    return /FBAN|FBAV|Instagram|WhatsApp|Line\/|TikTok|Musical|Snapchat|; wv\)/i.test(this.userAgent);
  }

  get isFirefox(): boolean {
    return /Firefox|FxiOS/i.test(this.userAgent);
  }

  get isSafari(): boolean {
    return /Safari/i.test(this.userAgent) && !/Chrome|CriOS|Edg|OPR|SamsungBrowser|Android/i.test(this.userAgent);
  }

  get subtitle(): string {
    if (this.isInAppBrowser) {
      return this.isIos
        ? `Open ${this.appName} in Safari, then add it to your Home Screen.`
        : `Open ${this.appName} outside this app. If it is already installed, it will open there.`;
    }
    if (this.isIos) {
      return `Tap Yes, install, then choose Add to Home Screen.`;
    }
    if (this.isFirefox) {
      return `Install ${this.appName} from the browser menu for a full-screen home-screen app.`;
    }
    if (this.isSafari) {
      return `Add ${this.appName} from the Share menu for a full-screen app.`;
    }
    return `Install ${this.appName} for faster access and a seamless full-screen experience.`;
  }

  get steps(): string[] {
    if (this.isInAppBrowser && this.isIos) {
      return ['Tap the browser menu', 'Choose Open in Safari', 'Tap Share, then Add to Home Screen'];
    }
    if (this.isInAppBrowser) {
      return ['Tap Open app', 'If installed, the home-screen app opens', 'Otherwise install from the browser menu'];
    }
    if (this.isIos) {
      return ['Tap the Share button', 'Choose Add to Home Screen', 'Tap Add'];
    }
    if (this.isFirefox) {
      return ['Tap the browser menu', 'Choose Install or Add to Home screen', 'Confirm to add the icon'];
    }
    if (this.isSafari) {
      return ['Click Share in the toolbar', 'Choose Add to Dock', 'Open it from the Dock'];
    }
    if (this.isAndroid) {
      return ['Tap the browser menu', 'Choose Install app or Add to Home screen', 'Confirm to add the icon'];
    }
    return ['Open the browser menu', 'Choose Install app', 'Confirm to add the icon'];
  }

  get installLabel(): string {
    if (this.showSteps) {
      return 'Got it';
    }
    if (this.isInAppBrowser) {
      return this.isIos ? 'Open Safari' : 'Open app';
    }
    return 'Yes, install';
  }

  later(): void {
    this.dialogRef.close('later');
  }

  async install(): Promise<void> {
    if (this.showSteps) {
      this.dialogRef.close('later');
      return;
    }

    if (this.isInAppBrowser) {
      if (this.isIos) {
        this.showSteps = true;
        return;
      }
      this.data?.openOutsideInAppBrowser?.();
      this.dialogRef.close('shared');
      return;
    }

    if (!this.isIos) {
      const outcome = (await this.data?.tryNativeInstall?.()) ?? 'unavailable';
      if (outcome === 'accepted') {
        this.dialogRef.close('installed');
        return;
      }
      if (outcome === 'dismissed') {
        this.dialogRef.close('later');
        return;
      }
    }

    // iOS and Mac Safari have no native install dialog. Open the share sheet from this tap.
    if ((this.isIos || this.isSafari) && typeof navigator.share === 'function') {
      try {
        await navigator.share({
          title: this.appName,
          text: `Add ${this.appName} to your Home Screen`,
          url: window.location.href,
        });
        this.dialogRef.close('shared');
      } catch (error) {
        if ((error as { name?: string })?.name !== 'AbortError') {
          this.showSteps = true;
        }
      }
      return;
    }

    this.showSteps = true;
  }
}
